// components/finance/AddRuleDialog.tsx
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Box,
  Divider,
  Alert,
  Chip,
  InputAdornment,
} from "@mui/material";
import Iconify from "../../components/iconify";
import { ITransactionCategory, ITransactionRule } from "../../@types/finance";
import { selectableTransactionCategories } from "../../utils/financeCategories";
import { ClinicService } from "../../api/clinic.service";

// ── Field/operator options ─────────────────────────────────────────────────────
const MATCH_FIELD_OPTIONS = [
  {
    value: "narrative",
    label: "Narrative",
    hint: "The raw bank narrative text",
  },
  {
    value: "counterparty",
    label: "Counterparty",
    hint: "Parsed payee / payer name",
  },
  {
    value: "transaction_type",
    label: "Transaction type",
    hint: "CR, BP, DD, VIS, TFR, DR",
  },
  {
    value: "amount_range",
    label: "Amount range",
    hint: "Match by absolute amount",
  },
];

const OPERATOR_OPTIONS: Record<string, { value: string; label: string }[]> = {
  narrative: [
    { value: "contains", label: "Contains" },
    { value: "starts_with", label: "Starts with" },
    { value: "equals", label: "Exactly equals" },
    { value: "regex", label: "Regex pattern" },
  ],
  counterparty: [
    { value: "contains", label: "Contains" },
    { value: "starts_with", label: "Starts with" },
    { value: "equals", label: "Exactly equals" },
  ],
  transaction_type: [{ value: "equals", label: "Is" }],
  amount_range: [
    { value: "between", label: "Between" },
    { value: "equals", label: "Exactly" },
  ],
};

const TX_TYPE_OPTIONS = ["CR", "BP", "DD", "VIS", "TFR", "DR"];

// ── Live preview helper ────────────────────────────────────────────────────────
function testRulePreview(
  field: string,
  operator: string,
  value: string,
  value2: string,
  sample: string,
): boolean {
  if (!value || !sample) return false;
  const s = sample.toLowerCase();
  const v = value.toLowerCase();
  switch (operator) {
    case "contains":
      return s.includes(v);
    case "starts_with":
      return s.startsWith(v);
    case "equals":
      return s === v;
    case "regex":
      try {
        return new RegExp(v, "i").test(sample);
      } catch {
        return false;
      }
    case "between": {
      const n = parseFloat(sample);
      return !isNaN(n) && n >= parseFloat(value) && n <= parseFloat(value2);
    }
    default:
      return false;
  }
}

// ── Component ──────────────────────────────────────────────────────────────────
interface Props {
  open: boolean;
  categories: ITransactionCategory[];
  onClose: () => void;
  onSave: (rule: Partial<ITransactionRule>) => Promise<void>;
}

export default function AddRuleDialog({
  open,
  categories,
  onClose,
  onSave,
}: Props) {
  const [matchField, setMatchField] = useState("counterparty");
  const [operator, setOperator] = useState("contains");
  const [matchValue, setMatchValue] = useState("");
  const [matchValue2, setMatchValue2] = useState("");
  const [categoryId, setCategoryId] = useState<number>(0);
  const [priority, setPriority] = useState(50);
  const [branchId, setBranchId] = useState<number>(0);
  const [contactName, setContactName] = useState("");
  const [clinics, setClinics] = useState<{ id: number; name: string }[]>([]);
  const [testSample, setTestSample] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset operator when field changes
  useEffect(() => {
    const opts = OPERATOR_OPTIONS[matchField] ?? [];
    if (opts.length > 0) setOperator(opts[0].value);
  }, [matchField]);

  // Load branches (clinics) for the assignment section
  useEffect(() => {
    if (!open) return;
    new ClinicService()
      .getAllClinics()
      .then((res) => setClinics(res.data ?? []))
      .catch(() => setClinics([]));
  }, [open]);

  const selectedCategory = categories.find((c) => c.id === categoryId);

  const previewMatch =
    testSample.length > 0 &&
    testRulePreview(matchField, operator, matchValue, matchValue2, testSample);

  const isValid = matchValue.trim() !== "" && categoryId !== 0;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    await onSave({
      matchField: matchField as ITransactionRule["matchField"],
      matchOperator: operator as ITransactionRule["matchOperator"],
      matchValue: matchValue.trim(),
      matchValue2: matchValue2.trim() || undefined,
      categoryId,
      priority,
      branchId: branchId !== 0 ? branchId : undefined,
      contactName: contactName.trim() || undefined,
    });
    setSaving(false);
    handleClose();
  };

  const handleClose = () => {
    setMatchField("counterparty");
    setOperator("contains");
    setMatchValue("");
    setMatchValue2("");
    setCategoryId(0);
    setPriority(50);
    setBranchId(0);
    setContactName("");
    setTestSample("");
    onClose();
  };

  const operatorOptions = OPERATOR_OPTIONS[matchField] ?? [];
  // A rule can never need to classify a bank transaction as in-app dispense
  // takings — that activity lives in the Dispense module's sales ledger.
  const ruleable = selectableTransactionCategories(categories);
  const incomeCategories = ruleable.filter((c) => c.type === "income");
  const expenditureCategories = ruleable.filter(
    (c) => c.type === "expenditure",
  );
  const financeCategories = ruleable.filter((c) => c.type === "finance");

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Classification Rule</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* ── Rule condition ── */}
          <Typography variant="subtitle2" color="text.secondary">
            Condition — when this matches…
          </Typography>

          <Stack direction="row" spacing={1.5}>
            <TextField
              select
              size="small"
              label="Match field"
              value={matchField}
              onChange={(e) => setMatchField(e.target.value)}
              sx={{ flex: 1 }}>
              {MATCH_FIELD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Stack>
                    <Typography variant="body2">{opt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {opt.hint}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Operator"
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
              sx={{ flex: 1 }}>
              {operatorOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* Match value */}
          {matchField === "transaction_type" ? (
            <TextField
              select
              fullWidth
              size="small"
              label="Transaction type"
              value={matchValue}
              onChange={(e) => setMatchValue(e.target.value)}>
              {TX_TYPE_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  <Typography fontFamily="monospace">{t}</Typography>
                </MenuItem>
              ))}
            </TextField>
          ) : operator === "between" ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                size="small"
                label="Min amount (£)"
                type="number"
                value={matchValue}
                onChange={(e) => setMatchValue(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">£</InputAdornment>
                  ),
                }}
              />
              <Typography variant="body2" color="text.secondary">
                and
              </Typography>
              <TextField
                size="small"
                label="Max amount (£)"
                type="number"
                value={matchValue2}
                onChange={(e) => setMatchValue2(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">£</InputAdornment>
                  ),
                }}
              />
            </Stack>
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Match value"
              value={matchValue}
              onChange={(e) => setMatchValue(e.target.value)}
              placeholder={
                operator === "regex" ? "e.g. ^WORLDPAY\\s+\\d+" : "e.g. ESSILOR"
              }
              helperText={
                operator === "regex"
                  ? "JavaScript regex — case-insensitive"
                  : "Case-insensitive match"
              }
            />
          )}

          <Divider />

          {/* ── Assignment ── */}
          <Typography variant="subtitle2" color="text.secondary">
            Assignment — classify as…
          </Typography>

          <TextField
            select
            fullWidth
            size="small"
            label="Category"
            value={categoryId}
            onChange={(e) => setCategoryId(Number(e.target.value))}>
            <MenuItem value={0} disabled>
              <Typography color="text.disabled">Select a category…</Typography>
            </MenuItem>

            <MenuItem
              disabled
              sx={{ fontSize: 11, color: "success.main", fontWeight: 700 }}>
              INCOME
            </MenuItem>
            {incomeCategories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  width="100%">
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "success.main",
                    }}
                  />
                  <Typography variant="body2">{c.name}</Typography>
                  {c.vatCode && (
                    <Chip
                      label={c.vatCode}
                      size="small"
                      sx={{ height: 16, fontSize: 10, ml: "auto" }}
                    />
                  )}
                </Stack>
              </MenuItem>
            ))}

            <MenuItem
              disabled
              sx={{ fontSize: 11, color: "error.main", fontWeight: 700 }}>
              EXPENDITURE
            </MenuItem>
            {expenditureCategories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  width="100%">
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "error.main",
                    }}
                  />
                  <Typography variant="body2">{c.name}</Typography>
                  {c.vatCode && (
                    <Chip
                      label={c.vatCode}
                      size="small"
                      sx={{ height: 16, fontSize: 10, ml: "auto" }}
                    />
                  )}
                </Stack>
              </MenuItem>
            ))}

            <MenuItem
              disabled
              sx={{ fontSize: 11, color: "secondary.main", fontWeight: 700 }}>
              FINANCE
            </MenuItem>
            {financeCategories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  width="100%">
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "secondary.main",
                    }}
                  />
                  <Typography variant="body2">{c.name}</Typography>
                  {c.vatCode && (
                    <Chip
                      label={c.vatCode}
                      size="small"
                      sx={{ height: 16, fontSize: 10, ml: "auto" }}
                    />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          {/* Priority */}
          <TextField
            size="small"
            type="number"
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            helperText="Lower number fires first. 10 = highest, 100 = lowest. Rules with the same priority run in creation order."
            inputProps={{ min: 1, max: 999 }}
          />

          <Stack direction="row" spacing={1.5}>
            <TextField
              select
              size="small"
              label="Branch"
              value={branchId}
              onChange={(e) => setBranchId(Number(e.target.value))}
              helperText="Optional — restrict this rule to one branch"
              sx={{ flex: 1 }}>
              <MenuItem value={0}>
                <Typography color="text.disabled">All branches</Typography>
              </MenuItem>
              {clinics.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Contact"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="e.g. Essilor Ltd"
              helperText="Optional — supplier/payee contact for this rule"
              sx={{ flex: 1 }}
            />
          </Stack>

          <Divider />

          {/* ── Live test ── */}
          <Typography variant="subtitle2" color="text.secondary">
            Live test — paste a sample narrative to preview
          </Typography>

          <TextField
            fullWidth
            size="small"
            label="Sample text"
            value={testSample}
            onChange={(e) => setTestSample(e.target.value)}
            placeholder="e.g. ESSILOR 014947"
            InputProps={{
              endAdornment:
                testSample.length > 0 ? (
                  <InputAdornment position="end">
                    <Iconify
                      icon={
                        previewMatch
                          ? "eva:checkmark-circle-fill"
                          : "eva:close-circle-fill"
                      }
                      sx={{
                        color: previewMatch ? "success.main" : "error.main",
                        width: 20,
                      }}
                    />
                  </InputAdornment>
                ) : null,
            }}
          />

          {testSample.length > 0 && (
            <Alert
              severity={previewMatch ? "success" : "error"}
              sx={{ borderRadius: 1 }}
              icon={
                <Iconify
                  icon={
                    previewMatch
                      ? "eva:checkmark-circle-fill"
                      : "eva:close-circle-fill"
                  }
                  width={18}
                />
              }>
              {previewMatch ? (
                <>
                  Rule matches — would classify as{" "}
                  <strong>
                    {selectedCategory?.name ?? "selected category"}
                  </strong>
                  {selectedCategory?.vatCode && (
                    <>
                      {" "}
                      with VAT code <strong>{selectedCategory.vatCode}</strong>
                    </>
                  )}
                </>
              ) : (
                "Rule does not match this sample"
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || saving}
          startIcon={
            saving ? (
              <Iconify icon="eva:loader-fill" width={16} />
            ) : (
              <Iconify icon="eva:plus-fill" width={16} />
            )
          }>
          {saving ? "Saving…" : "Create Rule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
